import { useEffect } from 'react';
import { $getRoot, $createParagraphNode } from 'lexical';
import {
  $getSelection,
  $isRangeSelection,
  $createTextNode,
} from '@fluentui-copilot/text-editor';

import { SearchCharacterList } from './entity-selection-utils';

interface IUseSetSelectedPromptItemProps {
  isDefaultValueEnabled: boolean;
  selectedPromptItem: any;
  editorRef: any;
  logger: any;
  setIsDefaultValueSet: (isDefaultValueSet: boolean) => void;
}

export const useSetSelectedPromptItem = ({
  isDefaultValueEnabled, selectedPromptItem, editorRef, logger, setIsDefaultValueSet,
}: IUseSetSelectedPromptItemProps) => {
  useEffect(() => {
    if (isDefaultValueEnabled && selectedPromptItem?.chatInputContent && editorRef.current) {
      editorRef.current.update(() => {
        const root = $getRoot();
        const existingContent = root.getTextContent();
        const defaultContent = selectedPromptItem.chatInputContent;

        let targetNode;
        let cursorOffset;
        let isSetDone = false;

        if (existingContent.trim() === '') {
          // No existing content - add default content
          root.clear();
          const paragraph = $createParagraphNode();
          const textNode = $createTextNode(defaultContent);
          paragraph.append(textNode);
          root.append(paragraph);
          targetNode = textNode;
        } else {
          // Existing content - append default content to the end
          const nodes = root.getAllTextNodes();
          const textNodes = nodes.filter(node => node.getType() === 'text');
          const lastNode = textNodes[textNodes.length - 1];
          const lastParagraph = lastNode.getParent();

          // Add space before appending if needed
          const lastNodeContent = lastNode.getTextContent();
          if (!lastNodeContent.endsWith(' ')) {
            lastNode.setTextContent(`${lastNodeContent} `);
          }

          const textNode = $createTextNode(defaultContent);
          lastParagraph.append(textNode);
          targetNode = textNode;
        }

        // Find the first occurrence of any character from SearchCharacterList
        const charIndices = SearchCharacterList.map(char => defaultContent.indexOf(char)).filter(index => index !== -1);

        if (charIndices.length > 0) {
          // Place cursor right after the first occurrence of any search character
          const firstSpecialCharIndex = Math.min(...charIndices);
          cursorOffset = firstSpecialCharIndex + 1;
        } else {
          // No special chars - place cursor at the end
          cursorOffset = defaultContent.length;
          isSetDone = true;
        }

        // Set cursor position
        const selection = $getSelection();
        if ($isRangeSelection(selection) && targetNode) {
          selection.anchor.set(targetNode.getKey(), cursorOffset, 'text');
          selection.focus.set(targetNode.getKey(), cursorOffset, 'text');
        }

        if (isSetDone) {
          setIsDefaultValueSet(true);
        }
      });

      logger?.trace(`[EntitySelection] Inserted default value: ${selectedPromptItem.chatInputContent}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDefaultValueEnabled, logger, selectedPromptItem]);
};
